import { Injectable, NgZone, signal, computed, effect, inject } from '@angular/core';
import { doc, onSnapshot } from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';

import { DataStoreService } from 'src/app/services/data-store/data-store.service';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationMemberService } from 'src/app/services/organization-member/organization-member.service';
import { PRODUCT_ID } from 'src/app/config/product';

export interface ProductAccessConfig {
  enabledProducts: string[];
  roleProductAccess: Record<string, string[]>;
}

/** Product IDs relevant to dev-console */
export const PRODUCT_IDS = {
  AI_STUDIO: 'ai-studio',
} as const;

/**
 * Pure access evaluator — single source of truth for the access cascade.
 * Must stay in sync with admin's semantics (products-access.component.ts).
 *
 *   1. No config → DENIED.
 *   2. Org level: `enabledProducts` must include this productId.
 *   3. Role level: if `roleProductAccess[roleId]` is a non-empty array, it's
 *      an allowlist for that role and must include this productId.
 *      Missing entry or empty array = no restriction for that role.
 */
export function evaluateAccess(
  config: ProductAccessConfig | null,
  roleId: string | null,
  productId: string,
): boolean {
  if (!config) return false;
  if (!config.enabledProducts.includes(productId)) return false;
  if (roleId) {
    const roleProducts = config.roleProductAccess[roleId];
    if (Array.isArray(roleProducts) && roleProducts.length > 0 && !roleProducts.includes(productId)) {
      return false;
    }
  }
  return true;
}

/**
 * Routes that remain accessible when AI Studio access is denied — anything
 * outside this list triggers a redirect to /product-disabled. Listed here:
 * the disabled page itself (avoid redirect loops), auth/onboarding flows,
 * and per-user settings the user still needs to manage their account / org.
 */
const SAFE_WHEN_DENIED_PREFIXES = [
  '/product-disabled',
  '/login', '/__/auth',
  '/organization-invitation',
  '/create-organization', '/organization',
  '/billing', '/usage',
  '/account', '/profile',
  '/aws-fulfillment',
];

function isSafeWhenDenied(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return SAFE_WHEN_DENIED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + '/'),
  );
}

@Injectable({
  providedIn: 'root',
})
export class ProductAccessService {
  private readonly config = signal<ProductAccessConfig>({ enabledProducts: [], roleProductAccess: {} });
  /** Public, read-only view of the org's enabled product IDs. */
  readonly enabledProducts = computed(() => this.config().enabledProducts);
  private readonly currentRoleId = signal<string | null>(null);
  private readonly loaded = signal(false);
  /** Whether the current user's member doc exists in the org. */
  private readonly memberExists = signal(true);

  private loadedOrgId: string | null = null;
  private configUnsubscribe: Unsubscribe | null = null;

  /**
   * Two-phase loading: both config and roleId must be received before
   * the service is considered loaded. This prevents a flash of incorrect
   * access state when the config arrives before the roleId (or vice versa).
   */
  private configReceived = false;
  private roleIdReceived = false;

  /**
   * Resolves when the service is fully loaded for the current org.
   * Starts unresolved so guards block on first refresh until real data
   * arrives — otherwise the guard sees `loaded=false` + an already-resolved
   * promise and routes through /product-disabled while data is still loading.
   */
  private loadedResolve: (() => void) | null = null;
  private loadedPromise: Promise<void> = new Promise((resolve) => {
    this.loadedResolve = resolve;
  });

  /** Emits when product access config finishes loading or changes. */
  readonly configLoaded$ = new BehaviorSubject<boolean>(false);

  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  constructor(
    private dataStore: DataStoreService,
    private firebase: FirebaseService,
    private organizationMemberService: OrganizationMemberService,
  ) {
    // Subscribe to real-time member changes for roleId
    this.organizationMemberService.currentMember$.subscribe(member => {
      this.memberExists.set(!!member);

      if (!member) {
        this.currentRoleId.set(null);
        // Don't mark roleIdReceived for null — wait for actual member data
        // (null can mean "not yet loaded for new org" or "member removed")
        // If we're already loaded, this is a live removal — notify subscribers
        if (this.loaded()) {
          this.configLoaded$.next(true);
        }
        return;
      }

      this.currentRoleId.set(member.roleId || member.role || null);
      this.roleIdReceived = true;

      if (this.loaded()) {
        // Already loaded — this is a live roleId change
        this.configLoaded$.next(true);
      } else {
        this.checkFullyLoaded();
      }
    });

    // Subscribe to org changes for config listening
    this.dataStore.getState$().subscribe(({ newState, oldState }) => {
      // Skip the initial empty state — `currentAccount` is `null` until
      // AccountService resolves it. Without this guard the no-org branch
      // below fires on app boot and grants studio access for one tick,
      // which bounces users off /product-disabled to /workflow on refresh.
      // On sign-out the same null arrives, but a listener is already active —
      // tear it down before the auth token invalidates, otherwise Firestore
      // reports permission-denied on the next snapshot.
      if (newState.currentAccount === null) {
        if (this.loadedOrgId) {
          this.stopListening();
          this.loadedOrgId = null;
          this.configReceived = false;
          this.roleIdReceived = false;
          this.loaded.set(false);
        }
        return;
      }

      const newOrgId = newState.currentAccount?.organizationId;
      const oldOrgId = oldState?.currentAccount?.organizationId;

      if (!newOrgId) {
        // No org = personal account, studio access only
        this.stopListening();
        this.config.set({ enabledProducts: [PRODUCT_ID], roleProductAccess: {} });
        this.currentRoleId.set(null);
        this.memberExists.set(true);
        this.loadedOrgId = null;
        this.configReceived = false;
        this.roleIdReceived = false;

        this.loaded.set(true);
        // Resolve any in-flight guard awaits before swapping in a fresh
        // already-resolved promise.
        this.loadedResolve?.();
        this.loadedResolve = null;
        this.loadedPromise = Promise.resolve();
        this.configLoaded$.next(true);
        return;
      }

      if (newOrgId !== oldOrgId || newOrgId !== this.loadedOrgId) {
        this.configReceived = false;
        this.roleIdReceived = false;

        this.loaded.set(false);
        // Don't reassign `loadedPromise` — guards that captured the previous
        // reference would stay stuck on a promise we no longer resolve.
        // Dev-console hard-reloads on org switch, so a single promise per
        // app session is sufficient; reuse the unresolved one from the
        // constructor.
        this.startListening(newOrgId);
      }
    });

    // Watch for AI Studio access — redirect whenever the user is on an
    // AI Studio route without access.  This covers:
    //   • Live revocation (admin disables product mid-session)
    //   • Org switch where the guard was bypassed (e.g. same-URL reload
    //     optimised away by the browser)
    //   • Any other race where the route activated before access was evaluated
    effect(() => {
      const cfg = this.config();
      const roleId = this.currentRoleId();
      const isLoaded = this.loaded();
      const hasMember = this.memberExists();

      if (!isLoaded) return;

      const hasAccess = hasMember && evaluateAccess(cfg, roleId, PRODUCT_ID);

      if (!hasAccess && !isSafeWhenDenied(this.router.url)) {
        this.router.navigate(['/product-disabled']);
      }
    });
  }

  /**
   * Returns a computed signal indicating whether the current user
   * can access the given product.
   */
  hasProductAccess(productId: string) {
    return computed(() => {
      // Block until config is loaded to prevent flash of unauthorized content
      if (!this.loaded()) return false;
      // Member removed from org — no access
      if (!this.memberExists()) return false;
      return evaluateAccess(this.config(), this.currentRoleId(), productId);
    });
  }

  /**
   * Async check for use in route guards.
   * Waits for the service to be fully loaded before evaluating access.
   */
  async checkProductAccess(productId: string): Promise<boolean> {
    if (!this.loaded()) {
      await this.loadedPromise;
    }

    return this.hasProductAccess(productId)();
  }

  private startListening(organizationId: string): void {
    this.stopListening();
    this.loadedOrgId = organizationId;

    const configRef = doc(
      this.firebase.firestore,
      'organizations', organizationId,
      'product-access', 'config',
    );

    this.configUnsubscribe = onSnapshot(configRef, (snapshot) => {
      this.ngZone.run(() => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          this.config.set({
            enabledProducts: data['enabledProducts'] || [],
            roleProductAccess: data['roleProductAccess'] || {},
          });
        } else {
          // No config doc = legacy org — default to studio only
          this.config.set({ enabledProducts: [PRODUCT_ID], roleProductAccess: {} });
        }

        this.configReceived = true;

        if (this.loaded()) {
          // Already loaded — this is a live config change
          this.configLoaded$.next(true);
        } else {
          this.checkFullyLoaded();
        }
      });
    }, (error) => {
      console.error('Product access config snapshot error:', error);
      if (this.isPhantomAuth(error)) {
        this.recoverFromPhantomAuth();
        return;
      }
      this.ngZone.run(() => {
        this.config.set({ enabledProducts: [PRODUCT_ID], roleProductAccess: {} });
        this.configReceived = true;
        this.checkFullyLoaded();
      });
    });
  }

  /**
   * "Phantom auth" = Firestore returns permission-denied while our cached
   * Firebase Auth user object is still set. This happens when IndexedDB is
   * wiped externally (e.g. user clears site data via DevTools) — the in-memory
   * user lingers but the underlying credentials are dead, so every listener
   * fails. Recovery: reload the page so Firebase Auth re-reads IndexedDB,
   * finds nothing, and signs the user out cleanly.
   */
  private isPhantomAuth(error: any): boolean {
    return error?.code === 'permission-denied' && !!this.firebase.auth.currentUser;
  }

  private phantomAuthRecoveryTriggered = false;
  private recoverFromPhantomAuth(): void {
    if (this.phantomAuthRecoveryTriggered) return;
    this.phantomAuthRecoveryTriggered = true;
    console.warn('[ProductAccess] Phantom auth detected (permission-denied while user still set) — reloading to recover');
    window.location.reload();
  }

  private stopListening(): void {
    if (this.configUnsubscribe) {
      this.configUnsubscribe();
      this.configUnsubscribe = null;
    }
  }

  private checkFullyLoaded(): void {
    if (this.configReceived && this.roleIdReceived) {
      this.loaded.set(true);
      this.configLoaded$.next(true);
      if (this.loadedResolve) {
        this.loadedResolve();
        this.loadedResolve = null;
      }
    }
  }
}
